import React, { useEffect, useState } from 'react';
import { Dropdown, Modal, Menu, message, Card, Input } from 'antd';
import { NavLink } from 'react-router-dom';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import coursesApiClient from 'utils/coursesApiClient';
import { config } from 'config/config';
import { FormattedMessage, useIntl } from 'react-intl';
import { MaterialType } from './types';
import FileDownload from 'js-file-download';
import ScormPreviewModal from 'pages/Courses/ActivitiesList/ScormPreviewModal';
import debounce from 'lodash.debounce';
import generalHelper from 'utils/generalHelpers';
import './styles.scss';

interface MaterialCardInterface {
    material: any;
    activityId: number;
    reload: Function;
    loading: React.Dispatch<React.SetStateAction<boolean>>;
    setShowMaterialModal: React.Dispatch<React.SetStateAction<boolean>>;
    setType: React.Dispatch<React.SetStateAction<MaterialType>>;
    setMaterial: React.Dispatch<React.SetStateAction<any>>;
    courseId: number;
}

const MaterialCard: React.FC<MaterialCardInterface> = ({
    material,
    activityId,
    reload,
    loading,
    setShowMaterialModal,
    setType,
    setMaterial,
    courseId,
}) => {
    const [showScormPreViewModal, setShowScormPreViewModal] = useState(false);
    const [title, setTitle] = useState<any>('');
    const [previewType, setPreviewType] = useState<'learn' | 'test'>('learn');
    const previewActivityId =
        material?.type === 'LEARNING_TEST'
            ? (material?.learningTestId ?? material?.learningTest?.id ?? 0)
            : activityId;

    const { confirm } = Modal;

    const intl = useIntl();

    useEffect(() => {
        switch (material.type) {
            case 'SCORM_LEARNING':
            case 'VIDEO_MATERIAL':
            case 'PDF_MATERIAL':
            case 'CERTIFICATE':
            case 'EMBEDDED_DOC':
            case 'FILE':
            case 'ASSIGNMENT':
            case 'ASSESSMENT':
                setTitle(intl.formatMessage({ id: 'courses.preview_learning_file' }));
                setPreviewType('learn');
                break;
            case 'SCORM_TEST':
            case 'LEARNING_TEST':
                setTitle(intl.formatMessage({ id: 'courses.preview_test_file' }));
                setPreviewType('test');
                break;
            default:
                break;
        }
    }, []);

    const warnDeletion = async () => {
        confirm({
            title: intl.formatMessage({ id: 'general.attention' }),
            content: intl.formatMessage({ id: 'courses.deletion_resource' }),
            icon: <ExclamationCircleOutlined />,
            okText: intl.formatMessage({ id: 'general.yes' }),
            cancelText: intl.formatMessage({ id: 'general.no' }),

            onOk() {
                deleteResource();
            },
        });
    };

    const deleteResource = async () => {
        loading(true);
        await coursesApiClient.request(
            `/api/v1/courses/${courseId}/activities/${activityId}/material/${material.id}/delete`,
            {},
            'DELETE'
        );
        reload(activityId);
    };

    const content = (
        <Menu>
            {[
                'SCORM_TEST',
                'SCORM_LEARNING',
                'PDF_MATERIAL',
                'ASSIGNMENT',
                'ASSESSMENT',
                'VIDEO_MATERIAL',
                'EMBEDDED_DOC',
                'CERTIFICATE',
            ].includes(material.type) ? (
                <>
                    <Menu.Item
                        key="edit"
                        onClick={() => {
                            setShowMaterialModal(true);
                            setType(material.type);
                            setMaterial(material);
                        }}
                    >
                        <FormattedMessage id="general.edit" />
                    </Menu.Item>
                    <Menu.Divider />
                </>
            ) : null}
            {material.type == 'LEARNING_TEST' ? (
                <>
                    <Menu.Item key="edit">
                        <NavLink
                            to={`/learning/tests/${material?.learningTestId}/edit`}
                            target="_blank"
                        >
                            <FormattedMessage id="general.edit" />
                        </NavLink>
                    </Menu.Item>
                    <Menu.Divider />
                </>
            ) : null}
            {[
                'SCORM_TEST',
                'SCORM_LEARNING',
                'LEARNING_TEST',
                'PDF_MATERIAL',
                'VIDEO_MATERIAL',
            ].includes(material.type) ? (
                <>
                    <Menu.Item
                        key="preview"
                        onClick={() => {
                            if (material?.type === 'LEARNING_TEST' && !previewActivityId) {
                                message.error(intl.formatMessage({ id: 'error.data_load' }));
                                return;
                            }
                            setShowScormPreViewModal(true);
                        }}
                    >
                        <FormattedMessage id="general.preview" />
                    </Menu.Item>
                    <Menu.Divider />
                </>
            ) : null}
            {['FILE'].includes(material.type) ? (
                <>
                    <Menu.Item key="download" onClick={() => downloadFile()}>
                        <FormattedMessage id="general.download" />
                    </Menu.Item>
                    <Menu.Divider />
                </>
            ) : null}
            <Menu.Item key="delete" onClick={() => warnDeletion()}>
                <FormattedMessage id="general.delete" />
            </Menu.Item>
        </Menu>
    );

    const downloadFile = async () => {
        try {
            const fileSettings = JSON.parse(material.fileSettings);

            const path = fileSettings?.path
                ? fileSettings?.path
                : fileSettings?.file_path
                ? fileSettings?.file_path
                : fileSettings?.filePath;

            let response = await coursesApiClient.request(
                `api/v1/courses/${courseId}/activities/${activityId}/material/storage/${path}`,
                {},
                'GET',
                true,
                true
            );
            FileDownload(response, fileSettings.title);
        } catch (error) {
            console.error(error);
            message.error(intl.formatMessage({ id: 'error.data_load' }));
        }
    };

    const updateSettings = async (settings: any, id: number) => {
        try {
            await coursesApiClient.request(
                `/api/v1/courses/${courseId}/activities/${activityId}/material/${id}`,
                settings,
                'PUT'
            );
        } catch (error) {
            console.error(error);
            message.error(intl.formatMessage({ id: 'error.data_load' }));
        }
    };

    const storeOptions = debounce(function (field: string, value: string, id: number) {
        updateSettings({ settings: { [field]: value }, type: 'FILE' }, id);
    }, 800);

    const panelContent = (type: string) => {
        let parsedFileSettings;
        let settings;

        switch (type) {
            case 'SCORM_LEARNING':
            case 'VIDEO_MATERIAL':
            case 'PDF_MATERIAL':
            case 'CERTIFICATE':
            case 'EMBEDDED_DOC':
                parsedFileSettings = material.fileSettings ? JSON.parse(material.fileSettings) : {};
                settings =
                    typeof material.settings === 'object'
                        ? material.settings
                        : JSON.parse(material.settings);

                return (
                    <>
                        <div className="card-title-wrapper">
                            <div className="card-title">
                                <i className="fa-solid fa-book"></i>{' '}
                                <FormattedMessage id="courses.learning_file" />
                            </div>
                            <Dropdown overlay={content} trigger={['click']}>
                                <div className="material-settings">
                                    <i className="fa-solid fa-ellipsis-vertical"></i>
                                </div>
                            </Dropdown>
                        </div>
                        <div className="material-details">
                            <div className="material-label">
                                <FormattedMessage id="general.title" />
                            </div>
                            <div className="material-title">
                                {parsedFileSettings.title ? (
                                    parsedFileSettings.title
                                ) : (
                                    <FormattedMessage id="general.activity" />
                                )}
                            </div>
                        </div>
                        <div className="material-details">
                            <div className="material-label">
                                <FormattedMessage id="general.type" />
                            </div>
                            <div>{material.type}</div>
                        </div>
                        {material.type === 'PDF_MATERIAL' && (
                            <div className="material-details">
                                <div className="material-label">
                                    <FormattedMessage id="courses.slide_length" />
                                </div>
                                <div>
                                    {settings?.seconds_on_slide ? settings.seconds_on_slide : '-'}
                                </div>
                            </div>
                        )}
                        {material.type === 'PDF_MATERIAL' && (
                            <div className="material-details">
                                <div className="material-label">
                                    <FormattedMessage id="courses.completion_coefficient" />
                                </div>
                                <div>
                                    {settings?.pass_rate && parsedFileSettings?.pages
                                        ? `${settings.pass_rate}/${parsedFileSettings.pages}`
                                        : '-'}
                                </div>
                            </div>
                        )}
                        {material.type === 'EMBEDDED_DOC' && (
                            <div className="material-details">
                                <div className="material-label">
                                    <FormattedMessage id="courses.min_seconds_on_file" />
                                </div>
                                <div>{settings?.secondsOnFile ? settings.secondsOnFile : '-'}</div>
                            </div>
                        )}
                    </>
                );
            case 'SCORM_TEST':
                parsedFileSettings = material.fileSettings ? JSON.parse(material.fileSettings) : {};
                settings =
                    typeof material.settings === 'object'
                        ? material.settings
                        : JSON.parse(material.settings);

                return (
                    <>
                        <div className="card-title-wrapper">
                            <div className="card-title">
                                <i className="fa-solid fa-check-double"></i>{' '}
                                <FormattedMessage id="courses.test_material" />
                            </div>
                            <Dropdown overlay={content} trigger={['click']}>
                                <div className="material-settings">
                                    <i className="fa-solid fa-ellipsis-vertical"></i>
                                </div>
                            </Dropdown>
                        </div>
                        <div className="material-details">
                            <div className="material-label">
                                <FormattedMessage id="general.title" />
                            </div>
                            <div className="material-title">
                                {parsedFileSettings.title ? (
                                    parsedFileSettings.title
                                ) : (
                                    <FormattedMessage id="general.activity" />
                                )}
                            </div>
                        </div>
                        <div className="material-details">
                            <div className="material-label">
                                <FormattedMessage id="general.type" />
                            </div>
                            <div>SCORM</div>
                        </div>
                        <div className="material-details">
                            <div className="material-label">
                                <FormattedMessage id="courses.min_test_result" />
                            </div>
                            <div>{settings?.pass_rate ? `${settings.pass_rate}%` : '-'}</div>
                        </div>
                    </>
                );
            case 'FILE':
                parsedFileSettings = material.fileSettings ? JSON.parse(material.fileSettings) : {};
                settings =
                    typeof material.settings === 'object'
                        ? material.settings
                        : JSON.parse(material.settings);

                return (
                    <>
                        <div className="card-title-wrapper">
                            <div className="card-title">
                                <i className="fa-solid fa-file-lines" />
                                <FormattedMessage id="courses.material" />
                            </div>
                            <Dropdown overlay={content} trigger={['click']}>
                                <div className="material-settings">
                                    <i className="fa-solid fa-ellipsis-vertical"></i>
                                </div>
                            </Dropdown>
                        </div>

                        <div className="material-details">
                            <div className="material-label">
                                <FormattedMessage id="general.title" />
                            </div>
                            <div className="material-title">
                                {parsedFileSettings.title ? (
                                    parsedFileSettings.title
                                ) : (
                                    <FormattedMessage id="general.file" />
                                )}
                            </div>
                        </div>

                        <div className="material-details">
                            <div className="material-label">
                                <FormattedMessage id="general.description" />
                            </div>
                            <div className="material-description-wrapper">
                                <Input
                                    maxLength={64}
                                    defaultValue={settings?.description ? settings.description : ''}
                                    onBlur={(event: any) =>
                                        reload
                                            ? storeOptions(
                                                  'description',
                                                  event.target.value,
                                                  material.id
                                              )
                                            : null
                                    }
                                    placeholder={intl.formatMessage({
                                        id: 'courses.file_description',
                                    })}
                                />
                            </div>
                        </div>

                        <div className="material-details">
                            <div className="material-label">
                                <FormattedMessage id="general.type" />
                            </div>
                            <div>
                                {parsedFileSettings?.title
                                    ? generalHelper.getFileType(parsedFileSettings.title)
                                    : '-'}
                            </div>
                        </div>

                        <div className="material-details">
                            <div className="material-label">
                                <FormattedMessage id="general.file_size" />
                            </div>
                            <div>
                                {parsedFileSettings?.file_size
                                    ? generalHelper.getFileSize(parsedFileSettings.file_size)
                                    : '-'}
                            </div>
                        </div>
                    </>
                );
            case 'ASSIGNMENT':
                parsedFileSettings = material.fileSettings ? JSON.parse(material.fileSettings) : {};
                settings =
                    typeof material.settings === 'object'
                        ? material.settings
                        : JSON.parse(material.settings);
                return (
                    <>
                        <div className="card-title-wrapper">
                            <div className="card-title">
                                <i className="fa-solid fa-file-signature"></i>
                                <FormattedMessage id="courses.individual_work" />
                            </div>
                            <Dropdown overlay={content} trigger={['click']}>
                                <div className="material-settings">
                                    <i className="fa-solid fa-ellipsis-vertical"></i>
                                </div>
                            </Dropdown>
                        </div>

                        <div className="material-details">
                            <div className="material-label">
                                <FormattedMessage id="general.title" />
                            </div>
                            <div className="material-title">
                                {settings.title ? (
                                    settings.title
                                ) : (
                                    <FormattedMessage id="general.activity" />
                                )}
                            </div>
                        </div>

                        <div className="material-details">
                            <div className="material-label">
                                <FormattedMessage id="courses.work_materials" />
                            </div>
                            <div>{parsedFileSettings.length}</div>
                        </div>

                        <div className="material-details">
                            <div className="material-label">
                                <FormattedMessage id="courses.work_submission" />
                            </div>
                            <div>
                                {!!settings.file_upload ? (
                                    <FormattedMessage id="general.yes" />
                                ) : (
                                    <FormattedMessage id="general.no" />
                                )}
                            </div>
                        </div>
                        <div className="material-details">
                            <div className="material-label">
                                <FormattedMessage id="courses.evaluation_type" />
                            </div>
                            <div>
                                {settings.evaluation_type === 'EXAMINATION' && (
                                    <FormattedMessage id="courses.examination" />
                                )}
                                {settings.evaluation_type === 'RANGE' && (
                                    <FormattedMessage id="courses.rating_range" />
                                )}
                                {settings.evaluation_type === 'NO_EVALUATION' && (
                                    <FormattedMessage id="courses.no_evaluation" />
                                )}
                            </div>
                        </div>
                    </>
                );
            case 'ASSESSMENT':
                settings =
                    typeof material.settings === 'object'
                        ? material.settings
                        : JSON.parse(material.settings);
                return (
                    <>
                        <div className="card-title-wrapper">
                            <div className="card-title">
                                <i className="fa-solid fa-file-user"></i>
                                <FormattedMessage id="assessment.practical_assessment" />
                            </div>
                            <Dropdown overlay={content} trigger={['click']}>
                                <div className="material-settings">
                                    <i className="fa-solid fa-ellipsis-vertical"></i>
                                </div>
                            </Dropdown>
                        </div>

                        <div className="material-details">
                            <div className="material-label">
                                <FormattedMessage id="general.title" />
                            </div>
                            <div className="material-title">
                                {settings.title ? (
                                    settings.title
                                ) : (
                                    <FormattedMessage id="general.activity" />
                                )}
                            </div>
                        </div>
                        <div className="material-details">
                            <div className="material-label">
                                <FormattedMessage id="courses.evaluation_type" />
                            </div>
                            <div>
                                {settings.evaluation_type === 'EXAMINATION' && (
                                    <FormattedMessage id="courses.examination" />
                                )}
                                {settings.evaluation_type === 'RANGE' && (
                                    <FormattedMessage id="courses.rating_range" />
                                )}
                            </div>
                        </div>
                        <div className="material-details">
                            <div className="material-label">
                                <FormattedMessage id="assessment.initiation_type" />
                            </div>
                            <div>
                                {settings.initiation_type === 'AUTOMATIC' && (
                                    <FormattedMessage id="assessment.automatic" />
                                )}
                                {settings.initiation_type === 'MANUAL' && (
                                    <FormattedMessage id="assessment.manual" />
                                )}
                            </div>
                        </div>
                    </>
                );
            case 'LEARNING_TEST':
                return (
                    <>
                        <div className="card-title-wrapper">
                            <div className="card-title">
                                <i className="fa-solid fa-circle-dot"></i>
                                <FormattedMessage id="learning_tests.learning_test" />
                            </div>
                            <Dropdown overlay={content} trigger={['click']}>
                                <div className="material-settings">
                                    <i className="fa-solid fa-ellipsis-vertical"></i>
                                </div>
                            </Dropdown>
                        </div>

                        <div className="material-details">
                            <div className="material-label">
                                <FormattedMessage id="general.title" />
                            </div>
                            <div className="material-title">{material.learningTest.title}</div>
                        </div>
                    </>
                );
            default:
                break;
        }
    };

    return (
        <>
            <Card className="material-card-wrapper">{panelContent(material.type)}</Card>

            <ScormPreviewModal
                title={title}
                visible={showScormPreViewModal}
                onCancel={() => setShowScormPreViewModal(false)}
                activityId={previewActivityId}
                previewType={previewType}
                type={material.type}
                courseId={courseId}
            />
        </>
    );
};

export default MaterialCard;
